import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OnboardingData } from '@zero/shared/util-models';
import { AuthService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { combineLatest, distinctUntilChanged, filter, take, tap } from 'rxjs';
import { onboardingActions } from '../+state/onboarding.actions';
import { onboardingFeature } from '../+state/onboarding.reducer';
import { selectValidResidences, selectValidTaxResidences } from '../+state/onboarding.selectors';
import { BankAccountInformation } from '../entities/bank-account-information';
import { NationalityTaxAndEmploymentInformation } from '../entities/nationality-tax-and-employment-information';
import { PersonalInformation } from '../entities/personal-information';
import { ProvisionInformation } from '../entities/provision-information';
import { ResidenceInformation } from '../entities/residence-information';

@Injectable({ providedIn: 'root' })
export class OnboardingFacade {
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #authService = inject(AuthService);

  readonly #stepMap: Record<number, string> = {
    1: commonRoutes.prelogin.onboarding.signUp,
    2: commonRoutes.prelogin.onboarding.enterPass,
    3: commonRoutes.prelogin.onboarding.emailConfirmation,
    4: commonRoutes.prelogin.onboarding.loginAfterEmailConfirmation,
    5: commonRoutes.postlogin.onboarding.redirect,
    6: commonRoutes.postlogin.onboarding.mobileNumber,
    7: commonRoutes.postlogin.onboarding.person,
    8: commonRoutes.postlogin.onboarding.residence,
    9: commonRoutes.postlogin.onboarding.nationality,
    10: commonRoutes.postlogin.onboarding.bankAccount,
    11: commonRoutes.postlogin.onboarding.initialDeposit,
    12: commonRoutes.postlogin.onboarding.provision,
    13: commonRoutes.postlogin.onboarding.ident,
    14: commonRoutes.postlogin.onboarding.inProgress,
  };

  public readonly totalSteps = Object.keys(this.#stepMap).length;

  constructor() {
    this.listenToStepChanges();
    this.loadRelevantData();
  }

  public listenToStepChanges = () =>
    combineLatest([this.step$, this.doStep$])
      .pipe(
        filter(([step, doStep]) => !!step && doStep),
        tap(() => this.#setStepDone()),
        distinctUntilChanged(),
      )
      .subscribe(([step]) => this.#router.navigate([this.#stepMap[step]], { relativeTo: this.#route }));

  public loadRelevantData = () =>
    combineLatest([this.#authService.isLoggedIn$(), this.#authService.isPotentialCustomer(), this.onboardingData$])
      .pipe(
        filter(([loggedIn, isPotentialCustomer, onboardingData]) => loggedIn && isPotentialCustomer && !onboardingData),
        take(1),
      )
      .subscribe(() => {
        this.loadCountries();
        this.loadOnboardingData();
      });

  public readonly step$ = this.#store.select(onboardingFeature.selectStep);
  public readonly doStep$ = this.#store.select(onboardingFeature.selectDoStep);
  public readonly email$ = this.#store.select(onboardingFeature.selectEmail);
  public readonly sendingEmail$ = this.#store.select(onboardingFeature.selectSendingEmail);
  public readonly sentEmailSuccessfully$ = this.#store.select(onboardingFeature.selectSentEmailSuccessfully);
  public readonly sentEmailError$ = this.#store.select(onboardingFeature.selectSentEmailError);
  public readonly sendingPassword$ = this.#store.select(onboardingFeature.selectSendingPassword);
  public readonly sentPasswordSuccessfully$ = this.#store.select(onboardingFeature.selectSentPasswordSuccessfully);
  public readonly sentPasswordError$ = this.#store.select(onboardingFeature.selectSentPasswordError);
  public readonly sendingConfirmationData$ = this.#store.select(onboardingFeature.selectSendingConfirmationData);
  public readonly sentConfirmationDataSuccessfully$ = this.#store.select(onboardingFeature.selectSentConfirmationDataSuccessfully);
  public readonly sentConfirmationDataError$ = this.#store.select(onboardingFeature.selectSentConfirmationDataError);
  public readonly loginSuccess$ = this.#store.select(onboardingFeature.selectLoginSuccess);
  public readonly loggingIn$ = this.#store.select(onboardingFeature.selectLoggingIn);
  public readonly loginErrorMessage$ = this.#store.select(onboardingFeature.selectLoginErrorMessage);
  public readonly onboardingData$ = this.#store.select(onboardingFeature.selectOnboardingData);
  public readonly onboardingDataLoading$ = this.#store.select(onboardingFeature.selectOnboardingDataLoading);
  public readonly sendingPhoneNumber$ = this.#store.select(onboardingFeature.selectSendingPhoneNumber);
  public readonly sentPhoneNumberSuccessfully$ = this.#store.select(onboardingFeature.selectSentPhoneNumberSuccessfully);
  public readonly isPhoneNumberVerified$ = this.#store.select(onboardingFeature.selectIsPhoneNumberVerified);
  public readonly isPhoneNumberVerifiedLoading$ = this.#store.select(onboardingFeature.selectIsPhoneNumberVerifiedLoading);
  public readonly verifyingCode$ = this.#store.select(onboardingFeature.selectVerifyingCode);
  public readonly sentCodeSuccessfully$ = this.#store.select(onboardingFeature.selectSentCodeSuccessfully);
  public readonly resendCodeInProgress$ = this.#store.select(onboardingFeature.selectResendCodeInProgress);
  public readonly resendCodeError$ = this.#store.select(onboardingFeature.selectResendCodeError);
  public readonly sendingInitialDeposit$ = this.#store.select(onboardingFeature.selectSendingInitialDeposit);
  public readonly doInitialDepositLater$ = this.#store.select(onboardingFeature.selectDoInitialDepositLater);
  public readonly sentInitialDepositSuccessfully$ = this.#store.select(onboardingFeature.selectSentInitialDepositSuccessfully);
  public readonly sendingPersonalInformation$ = this.#store.select(onboardingFeature.selectSendingPersonalInformation);
  public readonly sentPersonalInformationSuccessfully$ = this.#store.select(onboardingFeature.selectSentPersonalInformationSuccessfully);
  public readonly sendingResidenceInformation$ = this.#store.select(onboardingFeature.selectSendingResidenceInformation);
  public readonly sentResidenceInformationSuccessfully$ = this.#store.select(onboardingFeature.selectSentResidenceInformationSuccessfully);
  public readonly bankData$ = this.#store.select(onboardingFeature.selectBankData);
  public readonly sendingBankAccountInformation$ = this.#store.select(onboardingFeature.selectSendingBankAccountInformation);
  public readonly sentBankAccountInformationSuccessfully$ = this.#store.select(
    onboardingFeature.selectSentBankAccountInformationSuccessfully,
  );
  public readonly branches$ = this.#store.select(onboardingFeature.selectBranches);
  public readonly sendingNationalityTaxAndEmploymentInformation$ = this.#store.select(
    onboardingFeature.selectSendingNationalityTaxAndEmploymentInformation,
  );
  public readonly sentNationalityTaxAndEmploymentInformationSuccessfully$ = this.#store.select(
    onboardingFeature.selectSentNationalityTaxAndEmploymentInformationSuccessfully,
  );
  public readonly error$ = this.#store.select(onboardingFeature.selectError);
  public readonly sendingProvisionInformation$ = this.#store.select(onboardingFeature.selectSendingProvisionInformation);
  public readonly sentProvisionInformationSuccessfully$ = this.#store.select(onboardingFeature.selectSentProvisionInformationSuccessfully);
  public readonly countries$ = this.#store.select(onboardingFeature.selectCountries);
  public readonly validResidences$ = this.#store.select(selectValidResidences);
  public readonly validTaxResidences$ = this.#store.select(selectValidTaxResidences);
  public readonly deletingAllData$ = this.#store.select(onboardingFeature.selectDeletingAllData);
  public readonly deletedAllDataSuccessfully$ = this.#store.select(onboardingFeature.selectDeleteAllDataSuccessfully);

  public setStep = (step: number) => this.#store.dispatch(onboardingActions.setStep({ step }));
  public goToStep = (step: number) => this.#store.dispatch(onboardingActions.goToStep({ step }));
  public goToNextStep = () => this.#store.dispatch(onboardingActions.goToNextStep());
  public goToPreviousStep = () => this.#store.dispatch(onboardingActions.goToPreviousStep());
  public sendConfirmationData = (email: string, code: string, adAgreed: boolean) =>
    this.#store.dispatch(onboardingActions.sendConfirmationData({ email, code, adAgreed }));
  public sendEmail = (email: string) => this.#store.dispatch(onboardingActions.sendEmail({ email }));
  public signUp = (password: string) => this.#store.dispatch(onboardingActions.signUp({ password }));
  public login = (email: string, password: string) => this.#store.dispatch(onboardingActions.login({ email, password }));
  public loadOnboardingData = () => this.#store.dispatch(onboardingActions.loadOnboardingData());
  public sendPhoneNumber = (phoneNumber: string) => this.#store.dispatch(onboardingActions.sendPhoneNumber({ phoneNumber }));
  public checkPhoneNumberVerified = () => this.#store.dispatch(onboardingActions.checkPhoneNumberVerified());
  public verifyCode = (code: string) => this.#store.dispatch(onboardingActions.verifyCode({ code }));
  public resetSentCodeSuccessfully = () => this.#store.dispatch(onboardingActions.resetSentCodeSuccessfully());
  public resendCode = () => this.#store.dispatch(onboardingActions.resendCode());
  public sendInitialDeposit = (amount: number) => this.#store.dispatch(onboardingActions.sendInitialDeposit({ amount }));
  public doInitialDepositLater = () => this.#store.dispatch(onboardingActions.doInitialDepositLater());
  public getStepByRoute = (route: string) => Number(Object.entries(this.#stepMap).find(([, value]) => value === route)?.[0]);
  public sendPersonalInformation = (personalInformation: PersonalInformation) =>
    this.#store.dispatch(onboardingActions.sendPersonalInformation({ personalInformation }));
  public sendResidenceInformation = (residenceInformation: ResidenceInformation) =>
    this.#store.dispatch(onboardingActions.sendResidenceInformation({ residenceInformation }));
  public loadBankByIban = (iban: string) => this.#store.dispatch(onboardingActions.loadBankByIban({ iban }));
  public clearBankData = () => this.#store.dispatch(onboardingActions.clearBankData());
  public sendBankAccountInformation = (bankAccountInformation: BankAccountInformation) =>
    this.#store.dispatch(onboardingActions.sendBankAccountInformation({ bankAccountInformation }));
  public loadBranches = () => this.#store.dispatch(onboardingActions.loadBranches());
  public sendNationalityTaxAndEmploymentInformation = (nationalityTaxAndEmploymentInformation: NationalityTaxAndEmploymentInformation) =>
    this.#store.dispatch(onboardingActions.sendNationalityTaxAndEmploymentInformation({ nationalityTaxAndEmploymentInformation }));
  public sendProvisionInformation = (provisionInformation: ProvisionInformation) =>
    this.#store.dispatch(onboardingActions.sendProvisionInformation({ provisionInformation }));
  public loadCountries = () => this.#store.dispatch(onboardingActions.loadCountries());
  public deleteAllData = () => this.#store.dispatch(onboardingActions.deleteAllData());

  public getNextRouteBasedOnData(
    onboardingData: OnboardingData | null,
    isPhoneNumberVerified: boolean,
    doInitialDepositLater: boolean,
  ): string {
    if (!onboardingData) {
      return commonRoutes.postlogin.onboarding.redirect;
    }

    const { mobileNumber, person, residence, nationality, bankAccount, initialDeposit, ident, inProgress, provision } =
      commonRoutes.postlogin.onboarding;

    const conditions = {
      shouldSendMobileNumber: ({ cMobileNr }) => !cMobileNr,
      shouldVerifyMobileNumber: (verified: boolean) => !verified,
      shouldSendPersonData: ({ anrede, vorname, nachname, geburtsdatum, geburtsland, geburtsort }) =>
        !anrede || !vorname || !nachname || !geburtsdatum || !geburtsland || !geburtsort,
      shouldSendResidenceData: ({ strasse, hausnummer, plz, ort, land }) => !strasse || !hausnummer || !plz || !ort || !land,
      shouldSendNationalityData: ({ nationality, steuerland }: { nationality?: string; steuerland?: string }) =>
        !nationality || !steuerland,
      shouldSendBankAccountData: ({ kontoinhaber, iban }) => !kontoinhaber || !iban,
      needsInitialDeposit: ({
        status,
        directDebitPossible,
        directDebitAmount,
      }: {
        status: string;
        directDebitPossible?: boolean;
        directDebitAmount?: number;
      }) => (doInitialDepositLater || status.includes('IDENTIFICATION') ? false : directDebitPossible && !directDebitAmount),
      needsIdentification: ({ status }) =>
        ['IN_IDENTIFICATION', 'IDENTIFICATION_ABORTED', 'IDENTIFICATION_CANCELED', 'IDENTIFICATION_FRAUD_SUSPICION_CONFIRMED'].includes(
          status,
        ),
      isInIdentification: ({ status }) => status.startsWith('IDENTIFICATION_'),
    };

    const steps = [
      { check: () => conditions.shouldSendMobileNumber(onboardingData), step: mobileNumber },
      { check: () => conditions.shouldVerifyMobileNumber(isPhoneNumberVerified), step: mobileNumber },
      { check: () => conditions.shouldSendPersonData(onboardingData), step: person },
      { check: () => conditions.shouldSendResidenceData(onboardingData), step: residence },
      { check: () => conditions.shouldSendNationalityData(onboardingData), step: nationality },
      { check: () => conditions.shouldSendBankAccountData(onboardingData), step: bankAccount },
      { check: () => conditions.needsInitialDeposit(onboardingData), step: initialDeposit },
      { check: () => conditions.needsIdentification(onboardingData), step: ident },
      { check: () => conditions.isInIdentification(onboardingData), step: inProgress },
    ];

    const nextStep = steps.find(({ check }) => check());
    return nextStep ? nextStep.step : provision;
  }

  public goToStepByRoute = (route: string) => this.goToStep(this.#getStepByRoute(route));

  readonly #setStepDone = () => this.#store.dispatch(onboardingActions.setStepDone());

  readonly #getStepByRoute = (value: string): number | undefined => {
    const key = Object.keys(this.#stepMap).find((key) => this.#stepMap[Number(key)] === value);
    return key ? Number(key) : undefined;
  };
}
